import { Component, DestroyRef, afterNextRender, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { POLL_INTERVAL_MS, STORAGE_KEYS } from '../../../core/config/ui.constants';

@Component({
  selector: 'app-time-indicator',
  standalone: true,
  imports: [],
  templateUrl: './time-indicator.component.html',
  styleUrl: './time-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeIndicatorComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly currentTime = signal(new Date());
  /** Tema activo (refleja la clase .my-app-dark en <html>). */
  readonly isDark = signal(this.readDarkFromDom());

  readonly timeBadge = computed(() => {
    const hour = this.currentTime().getHours();
    const label = hour < 12 ? 'AM' : 'PM';
    const dark = this.isDark();

    // El icono refleja el TEMA activo (luna = oscuro, sol = claro) para que
    // funcione como indicador del toggle. La etiqueta AM/PM sigue la hora.
    return {
      label,
      icon: dark ? 'pi pi-moon' : 'pi pi-sun',
      className: dark
        ? 'time-indicator--night'
        : hour < 18
          ? 'time-indicator--morning'
          : 'time-indicator--afternoon',
    };
  });

  constructor() {
    afterNextRender(() => {
      // La preferencia aplicada antes de hidratar debe reflejarse inmediatamente en el botón.
      this.isDark.set(this.readDarkFromDom());
      const observer = new MutationObserver(() => this.isDark.set(this.readDarkFromDom()));
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
    interval(POLL_INTERVAL_MS.clock)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentTime.set(new Date());
        // Mantener sincronía si el modo automático cambió el tema por la hora.
        this.isDark.set(this.readDarkFromDom());
      });
  }

  /** Toggle manual claro/oscuro. Persiste la preferencia (sobre-escribe el auto). */
  toggleTheme(): void {
    const next = !this.readDarkFromDom();
    document.documentElement.classList.toggle('my-app-dark', next);
    this.isDark.set(next);
    try {
      localStorage.setItem(STORAGE_KEYS.theme, next ? 'dark' : 'light');
    } catch {
      /* almacenamiento no disponible: el cambio aplica solo a esta sesión */
    }
  }

  private readDarkFromDom(): boolean {
    return document.documentElement.classList.contains('my-app-dark');
  }
}
